import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Tag,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { announcementApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;

const AnnouncementManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [form] = Form.useForm();
  const { user } = useAuth();

  // 获取所有公告列表
  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await announcementApi.getAllAnnouncements(
        user?.username,
      );
      if (response && response.data && response.data.code === 200) {
        setAnnouncements(response.data.data || []);
      } else {
        message.error(response?.data?.message || '获取公告列表失败');
      }
    } catch (error) {
      console.error('获取公告列表失败:', error);
      message.error('获取公告列表失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // 获取目标用户类型显示文本
  const getTargetUserTypeText = (targetUserType) => {
    switch (targetUserType) {
      case 'ALL':
        return '所有用户';
      case 'NEW_USER':
        return '新用户';
      case 'OLD_USER':
        return '老用户';
      default:
        return targetUserType;
    }
  };

  // 获取优先级显示文本
  const getPriorityText = (priority) => {
    if (priority <= 1) return '紧急';
    if (priority <= 3) return '重要';
    return '一般';
  };

  // 获取优先级颜色
  const getPriorityColor = (priority) => {
    if (priority <= 1) return 'red';
    if (priority <= 3) return 'orange';
    return 'blue';
  };

  // 处理新建公告
  const handleCreate = () => {
    setEditingAnnouncement(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 处理编辑公告
  const handleEdit = (record) => {
    setEditingAnnouncement(record);
    form.setFieldsValue({
      title: record.title,
      content: record.content,
      targetUserType: record.targetUserType,
      priority: record.priority,
      status: record.status,
    });
    setModalVisible(true);
  };

  // 处理启用公告
  const handleEnable = async (id) => {
    try {
      const response = await announcementApi.enableAnnouncement(
        user?.username,
        id,
      );
      if (response && response.data && response.data.code === 200) {
        message.success('公告启用成功');
        fetchAnnouncements();
      } else {
        message.error(response?.data?.message || '公告启用失败');
      }
    } catch (error) {
      console.error('公告启用失败:', error);
      message.error('公告启用失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理禁用公告
  const handleDisable = async (id) => {
    try {
      const response = await announcementApi.disableAnnouncement(
        user?.username,
        id,
      );
      if (response && response.data && response.data.code === 200) {
        message.success('公告禁用成功');
        fetchAnnouncements();
      } else {
        message.error(response?.data?.message || '公告禁用失败');
      }
    } catch (error) {
      console.error('公告禁用失败:', error);
      message.error('公告禁用失败: ' + (error.message || '未知错误'));
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      let response;
      if (editingAnnouncement) {
        // 编辑公告
        response = await announcementApi.updateAnnouncement(user?.username, {
          id: editingAnnouncement.id,
          ...values,
        });
      } else {
        // 创建公告
        response = await announcementApi.createAnnouncement(
          user?.username,
          values,
        );
      }

      if (response && response.data && response.data.code === 200) {
        message.success(editingAnnouncement ? '公告编辑成功' : '公告创建成功');
        setModalVisible(false);
        form.resetFields();
        // 确保在状态更新后再刷新列表
        setTimeout(() => {
          fetchAnnouncements();
        }, 500);
      } else {
        message.error(
          response?.data?.message ||
            (editingAnnouncement ? '公告编辑失败' : '公告创建失败'),
        );
      }
    } catch (error) {
      console.error(
        editingAnnouncement ? '公告编辑失败:' : '公告创建失败:',
        error,
      );
      message.error(
        (editingAnnouncement ? '公告编辑失败: ' : '公告创建失败: ') +
          (error.message || '未知错误'),
      );
    }
  };

  // 公告状态渲染
  const renderStatus = (status) => {
    return status === 1 ? (
      <Tag color="green">启用</Tag>
    ) : (
      <Tag color="red">禁用</Tag>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      render: (text) => (
        <span>{(text || '').replace(/<[^>]*>/g, '').slice(0, 80)}</span>
      ),
    },
    {
      title: '目标用户',
      dataIndex: 'targetUserType',
      key: 'targetUserType',
      render: (text) => getTargetUserTypeText(text),
      width: 100,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityText(priority)}
        </Tag>
      ),
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: renderStatus,
      width: 80,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 1 ? (
            <Button
              icon={<StopOutlined />}
              size="small"
              onClick={() => handleDisable(record.id)}
            >
              禁用
            </Button>
          ) : (
            <Button
              icon={<CheckOutlined />}
              size="small"
              onClick={() => handleEnable(record.id)}
            >
              启用
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <section className="page-container">
      <header className="page-header">
        <div>
          <h1 className="page-title">公告管理</h1>
          <p className="page-description">
            维护客户公告，调整展示范围与优先级。
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建公告
        </Button>
      </header>
      <div className="page-content">
        <Table
          dataSource={announcements}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
          }}
          scroll={{ x: 1000 }}
        />
      </div>

      {/* 公告编辑/创建弹窗 */}
      <Modal
        title={editingAnnouncement ? '编辑公告' : '新建公告'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="公告标题"
            rules={[{ required: true, message: '请输入公告标题' }]}
          >
            <Input placeholder="请输入公告标题" />
          </Form.Item>

          <Form.Item
            name="content"
            label="公告内容"
            rules={[{ required: true, message: '请输入公告内容' }]}
          >
            <TextArea rows={4} placeholder="请输入公告内容" />
          </Form.Item>

          <Form.Item
            name="targetUserType"
            label="目标用户"
            rules={[{ required: true, message: '请选择目标用户' }]}
          >
            <Select placeholder="请选择目标用户">
              <Option value="ALL">所有用户</Option>
              <Option value="NEW_USER">新用户</Option>
              <Option value="OLD_USER">老用户</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="priority"
            label="优先级"
            rules={[{ required: true, message: '请输入优先级' }]}
            initialValue={3}
          >
            <InputNumber
              min={1}
              max={10}
              placeholder="数值越小优先级越高"
              style={{ width: '100%' }}
            />
          </Form.Item>

          {editingAnnouncement && (
            <Form.Item
              name="status"
              label="状态"
              rules={[{ required: true, message: '请选择状态' }]}
              initialValue={1}
            >
              <Select placeholder="请选择状态">
                <Option value={1}>启用</Option>
                <Option value={0}>禁用</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </section>
  );
};

export default AnnouncementManagement;
